import Input from './Input'
import { useState, useMemo } from "react";
import { _AM, _AuctionManager, _SGBF_ERC721 } from '../../../abi'
import { chainData } from "../../../chains";

import { useNavigate } from "@tanstack/react-router";
import { useAppKit, useAppKitAccount, useAppKitNetwork, useAppKitProvider } from '@reown/appkit/react'
import { toast } from "react-toastify";
import AddCommentOnly from "../../../components/Comments/AddCommentOnly.jsx"
import { ethers } from "ethers";
import { createListing } from "../../../lib/api";
import { BigNumber } from "bignumber.js";
import { sessionStorageGet } from '../../../lib/window-shim';

//let BN=new Web3Utils.

//
// Generate options declaratively.
const daysOptions = Array.from({ length: 31 }, (_, i) => ({ label: `${i}d`, value: i }));
const hourOptions = Array.from({ length: 24 }, (_, i) => ({ label: `${i}h`, value: i }));
const minsOptions = [
    { label: "1m", value: 1 },
    { label: "15m", value: 15 },
    { label: "30m", value: 30 },
    { label: "45m", value: 45 },
];

/**
 * CardForm component.
 *
 * Main form for listing an NFT item for sale or auction.
 * Features:
 * - Description and price fields
 * - Auction duration selection (days/hours/minutes) when showEnd=true
 * - Token approval flow
 * - Auction creation via AuctionManager contract
 * - Backend update via listItem
 * - Automatic comment and navigation to item page
 *
 * Uses ethers.js to interact with smart contracts.
 * Requires wallet connection and proper network.
 *
 * @param {Object} props - Component props
 * @param {Array} props.data - Form field definitions from formStructure (description, price)
 * @param {boolean} props.showEnd - If true, shows auction duration fields; otherwise fixed price
 * @param {string} props.media - Item image URL for social sharing
 * @returns {React.ReactElement} Listing form card
 */
const CardForm = ({data: initialData, showEnd, media}) => {

    const { open } = useAppKit();
    const { walletProvider } = useAppKitProvider("eip155");
    const { address, isConnected } = useAppKitAccount();
    const { chainId } = useAppKitNetwork();
    const navigate = useNavigate();

    // State for all form inputs, making them "controlled components".
    const [days, setDays] = useState(0);
    const [hours, setHour] = useState(0);
    const [mins, setMins] = useState(15);

    // Use a single state object for form fields for easier management.
    const [formState, setFormState] = useState({
        price: "",
        description: ""
    });

    // Safely get data from session storage once.
    const meta = useMemo(() => {
        const savedItem = sessionStorageGet("sellItem");
        return savedItem ? JSON.parse(savedItem) : null;
    }, []);

    // Memoize provider and contract instances to prevent re-creating them on every render.
    const provider = useMemo(() => walletProvider ? new ethers.providers.Web3Provider(walletProvider) : null, [walletProvider]);
    const signer = useMemo(() => provider?.getSigner(), [provider]);

    const AMContract = useMemo(() => {
        if (!signer || !meta?.network) return null;
        const amAddress = chainData[meta.network].aManager;
        return new ethers.Contract(amAddress, _AM, signer);
    }, [signer, meta?.network]);

    const GenERC721 = useMemo(() => {
        if (!signer || !meta?.bcd?.contract) return null;
        return new ethers.Contract(meta.bcd.contract, _SGBF_ERC721, signer);
    }, [signer, meta?.bcd?.contract]);

    // This useEffect handles side effects like showing toasts for wallet/network status.
    useEffect(() => {
        if (!isConnected) {
            toast.warn("Please connect your wallet to list an item.");
            return;
        }

        const requiredChainId = meta?.bcd?.chainInf?.chainId;
        const currentChainId = chainId ? parseInt(chainId, 16) : null;

        if (requiredChainId && currentChainId && requiredChainId.toString() !== currentChainId.toString()) {
            toast.error(`Please switch your wallet to the correct network (Chain ID: ${requiredChainId})`);
        }
    }, [isConnected, chainId, meta]);

    const setupListing = async (e) => {
        e.preventDefault();


        
        if (!isConnected || !AMContract || !GenERC721 || !meta) {
            toast.error("Cannot list item. Please ensure your wallet is connected and on the correct network.");
            return;
        }

        if (isNaN(parseFloat(formState.price)) || parseFloat(formState.price) <= 0) {
            toast.error("Please enter a valid price.");
            return;
        }

        // Use the `showEnd` prop to determine if this is an auction.
        const durationInSeconds = showEnd ? (86400 * days) + (3600 * hours) + (60 * mins) : (30 * 24 * 60 * 60);
        const finalizeOnReserveMet = !showEnd;

        try {
            const options = { value: "0", gasLimit: "8000000" };
            const saleAmount = ethers.utils.parseEther(formState.price.toString());
            const AMContractAddress = AMContract.address;

            // --- 1. Approval Flow ---
            toast.info("Checking token approval...");
            const approvedAddress = await GenERC721.getApproved(meta.bcd.tid);

            if (approvedAddress.toLowerCase() !== AMContractAddress.toLowerCase()) {
                toast.info("Requesting approval to list your NFT...");
                const approvalTx = await GenERC721.approve(AMContractAddress, meta.bcd.tid, options);
                await approvalTx.wait(1); // Wait for 1 confirmation
                toast.success("Approval successful!");
            } else {
                toast.info("Token is already approved.");
            }

            // --- 2. Create Auction ---
            toast.info("Creating the listing...");
            const createTx = await AMContract.createAuction(meta.bcd.contract, meta.bcd.tid, durationInSeconds, saleAmount.toString(), finalizeOnReserveMet, options);
            await createTx.wait(1);
            toast.success("Listing created successfully!");

            // --- 3. Update Backend ---
            const auctionData = await AMContract.getAuctionData(meta.bcd.contract, meta.bcd.tid);
            await createListing({ caOut: auctionData, meta });

            // --- 4. Add Comment & Redirect ---
            const finishTime = showEnd ? `in ${days}d, ${hours}h, ${mins}m` : "Fixed Price";
            const comment = `Desc: ${formState.description}\nPrice: ${formState.price}, Type: ${finishTime}`;

            // Determine chain slug from meta if available, else default to 'sgb'
            const netIdx = meta?.dbCdata?.network;
            const chainSlug = netIdx !== undefined
                ? (chainData[netIdx]?.tokName?.toLowerCase() || 'sgb')
                : 'sgb';
            const itemPath = `/item/${chainSlug}/${meta?.dbCdata?.id}/${meta?.bcd?.tid}`;

            await AddCommentOnly({
                metaAction: "Listed " + meta?.meta?.name,
                itemHash: itemPath,
                actionLocation: itemPath,
                actionMedia: { url: media, name: meta?.meta?.name },
                comment: comment
            });

            navigate({ to: itemPath });
        } catch (error) {
            console.error("Listing failed:", error);
            toast.error(error.reason || error.message || "An unknown error occurred during listing.");
        }
    }

    // const assoc = async () => {
    //
    // }

    /**
     * Handles input changes from the custom Input component.
     * This is designed to work with an Input that calls `onChangeHandler(event, id)`.
     */
    const formHandler = (event, id) => {
        // The first argument `event` is actually the input's `value`.
        // The second argument `id` is the loop index.
        if (id === 0) { // The first input in the map (id=0) is the description.
            setFormState(prevState => ({ ...prevState, description: event }));
        } else if (id === 1) { // The second input (id=1) is the price.
            setFormState(prevState => ({ ...prevState, price: event }));
        }
    };

    const endTimeHandler = (event, type) => {
        if (type === "days") {
            setDays(event.target.value)
        }
        if (type === "hours") {
            setHour(event.target.value)
        }
        if (type === "mins") {
            setMins(event.target.value)
        }
    }


    return (
        <div className="card card-purple">
            <div className="card-header pb-0 p-3">
                <h6 className="mb-0 text-light">Description and Price</h6>
            </div>
            <div className="card-body p-3">
                <form>

                    {/* Use initialData prop which is stable */}
                    {initialData && initialData.map((item, i) => (
                        <Input
                            className="input-purple"
                            onChangeHandler={formHandler}
                            key={i}
                            id={i}
                            textArea={item.textArea}
                            PlaceHolder={item?.PlaceHolder?.replace("[COIN]", chainData[meta?.network]?.tokName || 'COIN')}
                            type={item.type}
                        />
                    ))}
                    {/* Conditionally render the duration section based on the showEnd prop */}
                    {showEnd && (
                        <div id="durationDiv">
                        <div className="mt-2 position-relative">
                            <p className="text-sm">Set
                                auction end time:</p>
                        </div>
                        <select className="input-purple" onChange={(e) => endTimeHandler(e, "days")}>
                            <option value="0">0 Days</option>
                            {daysOptions.map((days) => <option value={days.value}>{days.label}</option>)}
                            ))</select>&nbsp;
                        <select  className="input-purple" onChange={(e) => endTimeHandler(e, "hours")}>
                            <option value="0">0 Hours</option>
                            {hourOptions.map((days) => <option value={days.value}>{days.label}</option>)}
                            ))</select>&nbsp;
                        <select  className="input-purple" onChange={(e) => endTimeHandler(e, "mins")}>
                            <option value="0">0 Mins</option>
                            {minsOptions.map((days) => <option value={days.value}>{days.label}</option>)}
                            ))</select>
                        </div>
                    )}

                    <div className="text-center">
                        <p className="text-sm ">
                            <br/>
                            Mobile users: Scroll down to set sale type (auction/buy it now)
                        </p>
                        {isConnected ?
                            <button id="listButton" onClick={(e) => setupListing(e)}
                                    className="btn bg-gradient-dark w-100 my-4 mb-2">List ITEM NOW
                            </button>
                            :
                            <button disabled id="listButton" onClick={(e) => setupListing(e)}
                                    className="btn bg-gradient-dark w-100 my-4 mb-2">Connect wallet first
                            </button>
                        }
                    </div>
                </form>
            </div>
        </div>
    );
}

export default CardForm;
